import {
  clearCartCacheAdapter,
  clearSavedForLaterCacheAdapter,
  getCurrentOrderAdapter,
  getSavedForLaterAdapter,
  LineItemOpRequest,
  refreshCartAdapter,
  setQuantitySyncMultiplierFromFulfillmentAttributes,
  updateAdapter
} from '@/api/cart-adapter'
import {
  cancelProofRequest,
  deleteProofsRequest,
  proofData,
  type ProofDatum
} from '@/api/commerce/proof'
import { error, exception } from '@/utils/logging'
import { useMercury } from '@/api/mercury/utils'
import { MutableCart } from '@/services/cart/mutableCart'

export type RecipientDeliveryMethod = 'MAILED' | 'SHIPPED'

export type CartLineItem = {
  uuid: string
  parentUuid: string | null
  customizationId: string | null
  itemId: number
  quantityRecipients: number
  quantityShippedWithoutRecipients: number
  recipientDeliveryMethod: RecipientDeliveryMethod | null
  quantity: number
  price: number
  total: number
  projectedMailDate: string | null
  projectedShipDate: string | null
  savedForLater: boolean
  minQuantity: number
  maxQuantity: number
  quantityIncrement: number
  shortTitle: string
  hugeImageUrl: string | null
  code: string
  created: string
  cartBehavior: string
  formatName: string
  scheduledShipDate: string | null
  quantitySyncMultiplier: number | null
}

export type CartLineItemWithProofDatum = CartLineItem & {
  proof?: ProofDatum
}

export type CartLineItemWithProofDatumAndChildren =
  CartLineItemWithProofDatum & {
    children: CartLineItemWithProofDatum[]
  }

const withProofData = async <T extends CartLineItem>(
  lineItems: T[]
): Promise<(T & { proof?: ProofDatum })[]> => {
  const proofIds = lineItems.map((item) => item.uuid)
  const proofs = await proofData(...proofIds)
  return lineItems.map((item) => {
    const proof = proofs[item.uuid]
    return { ...item, proof }
  })
}

/**
 * Returns an object with arrays of children, indexed by the `parentUuid` of each item.
 *
 * `null` is indexed as `'null'`, because `null` cannot be used as an index key.
 */
const byParentUuid = <T extends { parentUuid: string | null }>(
  items: T[]
): { [parentUuid: string]: T[] } =>
  items.reduce(
    (itemsByParentUuid, item) => {
      // null cannot be used as an index type, but 'null' will be OK since it's not a valid UUID
      const parentUuid = item.parentUuid ?? 'null'
      itemsByParentUuid[parentUuid] = (
        itemsByParentUuid[parentUuid] ?? []
      ).concat(item)
      return itemsByParentUuid
    },
    {} as { [parentUuid: string]: T[] }
  )

/**
 * Returns a tree of items as an array of root items (without parents),
 * each with an array of children.
 *
 * Can build a hierarchy from any flat array of CartLineItem objects.
 *
 * If referential integrity is violated in `items` passed in (i.e. there are orphans),
 * the result may not have a reference to all the original `items`.
 */
export const withChildren = <
  I extends { uuid: string; parentUuid: string | null },
  IC extends I & { children: IC[] }
>(
  items: I[]
): IC[] => {
  const itemsWithChildren = items.map(
    (item) =>
      ({
        ...item,
        children: [] as IC[]
      }) as IC
  )
  const lineItemsByParentUuid = byParentUuid(itemsWithChildren)
  const rootLineItems = lineItemsByParentUuid['null'] ?? []
  return rootLineItems.map((item) => {
    item.children = lineItemsByParentUuid[item.uuid] ?? []
    return item
  })
}

// TODO: remove this legacy support once avetti is removed and proofId do not exist everywhere
export interface LegacyLineItemOpRequest extends LineItemOpRequest {
  // TODO: Remove proofId from here and therefore all calling code
  proofId?: string
}

const getLineItemOpsWithArrayFormat = (
  lineItemOps: LegacyLineItemOpRequest[] | LegacyLineItemOpRequest
): LegacyLineItemOpRequest[] => {
  if (Array.isArray(lineItemOps) && lineItemOps.length === 0) {
    // We do not call api when empty array, so we add a warn to catch.
    // eslint-disable-next-line no-console
    console.warn('Empty line item ops are deprecated')
    throw new Error('Empty line item ops are deprecated')
  } else if (!Array.isArray(lineItemOps) && lineItemOps) {
    return [lineItemOps]
  } else {
    return lineItemOps
  }
}

const duplicatesHandler = (duplicates: LineItemOpRequest[]) => {
  if (duplicates.length) {
    if (useMercury()) {
      throw new Error('Duplicate UUIDs detected in cart for Mercury', {
        cause: {
          duplicates
        }
      })
    } else {
      error('Duplicate UUIDs detected in cart', {
        duplicates
      })
    }
  }
}

const orphansHandler = (orphans: LineItemOpRequest[]) => {
  orphans.forEach((orphan) => {
    error('System automatically deleted orphan', {
      orphan
    })
  })
}

/**
 * Expands line item ops using `MutableCart` to evaluate client-side rules
 * to ensure cart integrity and evaluate rules for children and quantity sync behavior.
 */
const getExpandedLineItemOps = async (
  lineItemOps: LegacyLineItemOpRequest[]
): Promise<LineItemOpRequest[]> => {
  // Get both cart and savedForLater in parallel, and start item request for each section ASAP
  const cart = getCurrentOrder().then((cart) =>
    setQuantitySyncMultiplierFromFulfillmentAttributes(cart || [])
  )
  const savedForLater = getSavedForLater().then((savedForLater) =>
    setQuantitySyncMultiplierFromFulfillmentAttributes(savedForLater || [])
  )

  const [cartWithMultiplier, savedForLaterWithMultiplier] = await Promise.all([
    cart,
    savedForLater
  ])

  const mutableCart = await MutableCart.create(
    {
      cart: cartWithMultiplier,
      savedForLater: savedForLaterWithMultiplier
    },
    lineItemOps
  )

  duplicatesHandler(mutableCart.duplicates)
  orphansHandler(mutableCart.orphans)

  return mutableCart.commitOps()
}

/**
 * Finds the line items what have 'Cancel' action, executes the cancellation,
 * and returns the promise to be handled by the caller.
 */
const cancelProofs = (
  lineItemOps: LegacyLineItemOpRequest[]
): Promise<void>[] => {
  return (
    lineItemOps
      .filter(
        (lineItem) =>
          lineItem._destroy &&
          lineItem._destroyProofAction === 'Cancel' &&
          lineItem.uuid
      )
      // proofToRemove.uuid is string at this point because of the previous filter, so we use it directly and cast the type
      .map(async (proofToRemove) => {
        await cancelProofRequest(proofToRemove.uuid!)
      })
  )
}

/**
 * Finds the line items what have 'Delete' action, executes the deletion,
 * and returns the promise to be handled by the caller.
 *
 * The result of this function is designed to be handled by Promise.all()
 *
 * e.g.
 * - Promise.all(deleteProofs(lineItemOps))
 * - Promise.all([...deleteProofs(lineItemOps)])
 */
const deleteProofs = (
  lineItemOps: LegacyLineItemOpRequest[]
): Promise<void>[] => {
  const proofsToDelete = lineItemOps
    .filter(
      (removeProof) =>
        removeProof._destroyProofAction === 'Delete' &&
        typeof removeProof?.uuid == 'string'
    )
    .map((proof) => proof.uuid as string)

  // We handle this response as an array to avoid problems if there is nothing to delete.
  // In this case, A Promise.all([]) will resolve immediately instead of have to
  // check if this function returns any promise.
  if (proofsToDelete.length) {
    return [deleteProofsRequest(proofsToDelete).then(() => {})]
  } else {
    return []
  }
}

/**
 * Update the cart.
 *
 * @param _lineItemOps Operations to commit.
 * These will be expanded client-side for Avetti to process operations related to children.
 * Use `null` to run any fixes to the initial state.
 */
export const update = async (
  _lineItemOps: LegacyLineItemOpRequest[] | LegacyLineItemOpRequest | null
) => {
  try {
    const lineItemOps =
      _lineItemOps !== null ? getLineItemOpsWithArrayFormat(_lineItemOps) : []

    const expandedLineItemOps = await getExpandedLineItemOps(lineItemOps)

    if (expandedLineItemOps.length === 0) {
      // close the operations since there is no things to do
      return
    }

    const cancelAndDeleteRequests = [
      ...cancelProofs(expandedLineItemOps),
      ...deleteProofs(expandedLineItemOps)
    ]
    await updateAdapter(expandedLineItemOps)
    clearCartAndSavedForLaterCache()

    await Promise.all(cancelAndDeleteRequests)
  } catch (error) {
    if ((error as Error).message !== 'Empty line item ops are deprecated') {
      exception(error, {
        lineItemOps: _lineItemOps
      })
      throw error
    }
  }
}

/**
 * Gets saved for later line items.
 * Returns `null` if there is no saved for later order, i.e. nothing has ever been saved for later.
 */
export const getSavedForLater = async (): Promise<CartLineItem[] | null> => {
  try {
    return await getSavedForLaterAdapter()
  } catch (error) {
    exception(error)
    throw error
  }
}

/**
 * Gets the line items in the current order (cart).
 * Returns `null` if there is no current order, i.e. nothing has ever been added to the cart,
 * or there's no incomplete order.
 */
export const getCurrentOrder = async (): Promise<CartLineItem[] | null> => {
  try {
    return await getCurrentOrderAdapter()
  } catch (error) {
    exception(error)
    throw error
  }
}

/**
 * Gets the cart and saved for later items in parallel.
 */
export const getCartAndSavedForLater = async (): Promise<{
  cart: CartLineItem[]
  savedForLater: CartLineItem[]
}> => {
  // Get both in parallel
  const [cart, savedForLater] = await Promise.all([
    getCurrentOrder(),
    getSavedForLater()
  ])
  return {
    cart: cart ?? [],
    savedForLater: savedForLater ?? []
  }
}

const withProofDataAndChildren = async (
  itemResponse: CartLineItem[] | null
): Promise<CartLineItemWithProofDatumAndChildren[]> => {
  const items = itemResponse ?? []
  const itemsWithProofData = await withProofData(items)
  return withChildren(itemsWithProofData)
}

/**
 * Returns the root cart and saved for later items, enriched with proof data, with nested children.
 *
 * Network requests are cached are parallelized when possible, but since multiple requests may be made,
 * only use this (versus only cart, or only saved for later, or getting line items without proof data)
 * when all the data is necessary.
 */
export async function getCartAndSavedForLaterTreesWithProofData(): Promise<{
  cart: CartLineItemWithProofDatumAndChildren[]
  savedForLater: CartLineItemWithProofDatumAndChildren[]
}> {
  // Get both in parallel, and start proof data fetching for each section ASAP
  const [cart, savedForLater] = await Promise.all([
    getCurrentOrder().then(withProofDataAndChildren),
    getSavedForLater().then(withProofDataAndChildren)
  ])
  return {
    cart,
    savedForLater
  }
}

/**
 * Convenience method to move an item to saved for later using update method
 */
export function moveToSavedForLater(uuid: string): Promise<void> {
  return update([{ uuid, savedForLater: true }])
}

/**
 * Convenience method to move an item to cart for later using update method
 */
export function moveToCart(uuid: string): Promise<void> {
  return update([{ uuid, savedForLater: false }])
}

/**
 * Convenience method to remove an item using update method
 */
export function remove(uuid: string): Promise<void> {
  return update([{ uuid, _destroy: true }])
}

/**
 * Convenience set the quantity of an item using update method
 */
export function setQuantity(uuid: string, quantity: number): Promise<void> {
  return update([{ uuid, quantity }])
}

/**
 * Convenience method that increments the quantity of a line item.
 *
 * If the quantity to increment by is negative, an existing line item's quantity will be decremented,
 * but a negative quantity is not valid if no line item with the variant ID exists/
 * If the final decremented quantity is < 0, the line item will be deleted.
 *
 * @param quantity The quantity to increment (or decrement if negative) by.
 */
export function incrementQuantity(
  itemId: number,
  quantity: number
): Promise<void> {
  return update([{ itemId, quantity }])
}

export async function refreshCart(): Promise<void> {
  await refreshCartAdapter()
  clearCartAndSavedForLaterCache()
}

export function clearCartAndSavedForLaterCache(): void {
  clearCartCacheAdapter()
  clearSavedForLaterCacheAdapter()
}

export const findLineItemTreeWithProofData = async (
  uuid: string
): Promise<CartLineItemWithProofDatumAndChildren | null> => {
  const { cart, savedForLater } =
    await getCartAndSavedForLaterTreesWithProofData()
  const cartPlusSaved = [...cart, ...savedForLater]
  return cartPlusSaved.find((cartItem) => cartItem.uuid === uuid) ?? null
}
